/**
 * Complaint Detail Page — F008
 *
 * Full complaint lifecycle view:
 * - Summary card (complainant info, type, category)
 * - Deadline cards (ack + resolution countdowns with red urgency)
 * - Workflow actions: Acknowledge, Resolve, Escalate to AFCA
 * - Notes timeline (chronological)
 * - AFCA referral section (when escalated)
 * - Edit modal (ComplaintModal in edit mode)
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { format, differenceInHours, differenceInDays } from "date-fns";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock,
  Edit,
  FileText,
  Loader2,
  MessageSquare,
  Plus,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceLayout } from "./compliance-layout";
import { ComplaintModal } from "@/components/modals/complaint-modal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ComplaintNote {
  id: string;
  complaintId: string;
  noteType: string;
  noteContent: string;
  communicationMethod: string | null;
  communicationDirection: string | null;
  createdAt: string;
  createdBy: string | null;
}

interface AfcaReferral {
  id: string;
  complaintId: string;
  afcaReference: string | null;
  afcaStatus: string;
  referralDate: string;
  responseDueDate: string | null;
  createdAt: string;
}

interface ComplaintDetail {
  id: string;
  complaintReference: string | null;
  complaintType: string;
  complaintCategory: string;
  complaintStatus: string;
  complainantType: string | null;
  complainantName: string | null;
  complainantEmail: string | null;
  complainantPhone: string | null;
  dealId: string | null;
  receivedDate: string;
  acknowledgedDate: string | null;
  dueDate: string | null;
  resolvedDate: string | null;
  outcome: string | null;
  resolutionSummary: string | null;
  remediationActions: string | null;
  escalatedToAfca: boolean;
  afcaReference: string | null;
  createdAt: string;
  notes: ComplaintNote[];
  afcaReferrals: AfcaReferral[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  open: {
    label: "Open",
    className: "bg-slate-100 text-slate-700 border-slate-300",
  },
  acknowledged: {
    label: "Acknowledged",
    className: "bg-blue-100 text-blue-700 border-blue-300",
  },
  investigating: {
    label: "Investigating",
    className: "bg-yellow-100 text-yellow-700 border-yellow-300",
  },
  resolved: {
    label: "Resolved",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  escalated: {
    label: "Escalated",
    className: "bg-red-100 text-red-700 border-red-300",
  },
};

const TYPE_LABELS: Record<string, string> = {
  standard: "Standard",
  hardship: "Hardship",
  default_notice: "Default Notice",
};

const CATEGORY_LABELS: Record<string, string> = {
  service: "Service",
  product: "Product",
  fees: "Fees",
  communication: "Communication",
  advice: "Advice",
  privacy: "Privacy",
  other: "Other",
};

const OUTCOME_LABELS: Record<string, string> = {
  upheld: "Upheld",
  partially_upheld: "Partially Upheld",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

const NOTE_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  internal: {
    label: "Internal",
    className: "bg-gray-100 text-gray-700 border-gray-300",
  },
  communication: {
    label: "Communication",
    className: "bg-blue-100 text-blue-700 border-blue-300",
  },
  investigation: {
    label: "Investigation",
    className: "bg-purple-100 text-purple-700 border-purple-300",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy h:mm a");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Deadline Card
// ---------------------------------------------------------------------------

function DeadlineCard({ complaint }: { complaint: ComplaintDetail }) {
  const now = new Date();

  const renderAckDeadline = () => {
    if (complaint.acknowledgedDate) {
      return (
        <div className="text-sm text-green-600 flex items-center gap-1">
          <Check className="h-3 w-3" />
          Acknowledged {formatDate(complaint.acknowledgedDate)}
        </div>
      );
    }

    const receivedAt = new Date(complaint.receivedDate);
    const ackDueAt = new Date(receivedAt);
    ackDueAt.setDate(ackDueAt.getDate() + 1);
    const hoursLeft = differenceInHours(ackDueAt, now);

    if (hoursLeft < 0) {
      return (
        <div className="text-sm font-semibold text-red-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          OVERDUE — {formatDate(ackDueAt.toISOString())}
        </div>
      );
    }
    if (hoursLeft <= 24) {
      return (
        <div className="text-sm font-semibold text-red-600 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Due in {hoursLeft}h — {formatDate(ackDueAt.toISOString())}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground">
        Due {formatDate(ackDueAt.toISOString())}
      </div>
    );
  };

  const renderResolutionDeadline = () => {
    if (complaint.resolvedDate) {
      return (
        <div className="text-sm text-green-600 flex items-center gap-1">
          <Check className="h-3 w-3" />
          Resolved {formatDate(complaint.resolvedDate)}
        </div>
      );
    }

    if (!complaint.dueDate) {
      return (
        <div className="text-sm text-muted-foreground" data-testid="resolution-due-display">
          No due date set
        </div>
      );
    }

    const dueAt = new Date(complaint.dueDate);
    const daysLeft = differenceInDays(dueAt, now);

    if (daysLeft < 0) {
      return (
        <div
          className="text-sm font-semibold text-red-600 flex items-center gap-1"
          data-testid="resolution-due-display"
        >
          <AlertTriangle className="h-3 w-3" />
          OVERDUE — {formatDate(complaint.dueDate)}
        </div>
      );
    }
    if (daysLeft <= 7) {
      return (
        <div
          className="text-sm font-semibold text-red-600 flex items-center gap-1"
          data-testid="resolution-due-display"
        >
          <Clock className="h-3 w-3" />
          {daysLeft}d left — {formatDate(complaint.dueDate)}
        </div>
      );
    }
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="resolution-due-display"
      >
        Due {formatDate(complaint.dueDate)} ({daysLeft} days)
      </div>
    );
  };

  return (
    <Card data-testid="complaint-deadline-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Deadlines
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Acknowledgement Due
          </div>
          {renderAckDeadline()}
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Resolution Due
          </div>
          {renderResolutionDeadline()}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

function TimelineItem({ note }: { note: ComplaintNote }) {
  const config = NOTE_TYPE_CONFIG[note.noteType] ?? {
    label: note.noteType,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <div className="flex gap-3" data-testid={`timeline-note-${note.id}`}>
      <div className="flex flex-col items-center">
        <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
          <MessageSquare className="h-3 w-3 text-muted-foreground" />
        </div>
        <div className="w-px flex-1 bg-border mt-1" />
      </div>
      <div className="pb-4 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="outline" className={`text-xs ${config.className}`}>
            {config.label}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(note.createdAt)}
          </span>
          {note.communicationMethod && (
            <span className="text-xs text-muted-foreground">
              via {note.communicationMethod}
            </span>
          )}
          {note.communicationDirection && (
            <span className="text-xs text-muted-foreground capitalize">
              ({note.communicationDirection})
            </span>
          )}
        </div>
        <p className="text-sm">{note.noteContent}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Acknowledge Dialog
// ---------------------------------------------------------------------------

function AcknowledgeDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (method: string, date: string) => void;
  isPending: boolean;
}) {
  const [method, setMethod] = useState("email");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setMethod("email");
      setDate(new Date().toISOString().split("T")[0]);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="dialog-acknowledge">
        <DialogHeader>
          <DialogTitle>Record Acknowledgement</DialogTitle>
          <DialogDescription>
            Record how and when the complaint was acknowledged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Acknowledgement Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="select-acknowledgement-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="letter">Letter</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ack-date">Acknowledgement Date</Label>
            <Input
              id="ack-date"
              data-testid="input-acknowledgement-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-submit-acknowledge"
            onClick={() => onSubmit(method, date)}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? "Recording..." : "Record Acknowledgement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Resolve Dialog
// ---------------------------------------------------------------------------

function ResolveDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (outcome: string, summary: string, remedies: string, afcaRights: boolean) => void;
  isPending: boolean;
}) {
  const [outcome, setOutcome] = useState("");
  const [summary, setSummary] = useState("");
  const [remedies, setRemedies] = useState("");
  const [afcaRights, setAfcaRights] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setOutcome("");
      setSummary("");
      setRemedies("");
      setAfcaRights(false);
    }
    onOpenChange(newOpen);
  };

  const isValid = outcome.length > 0 && summary.trim().length > 0 && afcaRights;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="dialog-resolve">
        <DialogHeader>
          <DialogTitle>Record Resolution</DialogTitle>
          <DialogDescription>
            Record the outcome and resolution details for this complaint.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>
              Outcome <span className="text-destructive">*</span>
            </Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="select-outcome">
                <SelectValue placeholder="Select outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upheld">Upheld</SelectItem>
                <SelectItem value="partially_upheld">Partially Upheld</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="withdrawn">Withdrawn</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resolution-summary">
              Resolution Summary <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="resolution-summary"
              data-testid="input-resolution-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Summary of how the complaint was resolved..."
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="remedies">Remedies Provided</Label>
            <Textarea
              id="remedies"
              data-testid="input-remedies"
              value={remedies}
              onChange={(e) => setRemedies(e.target.value)}
              placeholder="Any compensation, refunds or remedies provided..."
              rows={2}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="afca-rights"
              data-testid="checkbox-afca-rights"
              checked={afcaRights}
              onCheckedChange={(checked) => setAfcaRights(checked as boolean)}
            />
            <Label htmlFor="afca-rights" className="text-sm">
              AFCA rights advised to complainant{" "}
              <span className="text-destructive">*</span>
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-submit-resolve"
            onClick={() => onSubmit(outcome, summary, remedies, afcaRights)}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? "Recording..." : "Record Resolution"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Escalate Dialog
// ---------------------------------------------------------------------------

function EscalateDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (afcaRef: string, referralDate: string) => void;
  isPending: boolean;
}) {
  const [afcaRef, setAfcaRef] = useState("");
  const [referralDate, setReferralDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAfcaRef("");
      setReferralDate(new Date().toISOString().split("T")[0]);
    }
    onOpenChange(newOpen);
  };

  const isValid = afcaRef.trim().length > 0 && referralDate.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="dialog-escalate">
        <DialogHeader>
          <DialogTitle>Escalate to AFCA</DialogTitle>
          <DialogDescription>
            This will create an AFCA case and change complaint status to
            escalated.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="afca-reference">
              AFCA Reference <span className="text-destructive">*</span>
            </Label>
            <Input
              id="afca-reference"
              data-testid="input-afca-reference"
              value={afcaRef}
              onChange={(e) => setAfcaRef(e.target.value)}
              placeholder="e.g. AFCA-2026-12345"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="referral-date">
              Referral Date <span className="text-destructive">*</span>
            </Label>
            <Input
              id="referral-date"
              data-testid="input-referral-date"
              type="date"
              value={referralDate}
              onChange={(e) => setReferralDate(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-submit-escalate"
            onClick={() => onSubmit(afcaRef, referralDate)}
            disabled={!isValid || isPending}
            variant="destructive"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? "Escalating..." : "Escalate to AFCA"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add Note Dialog
// ---------------------------------------------------------------------------

function AddNoteDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    noteType: string,
    content: string,
    method: string,
    direction: string
  ) => void;
  isPending: boolean;
}) {
  const [noteType, setNoteType] = useState("internal");
  const [content, setContent] = useState("");
  const [method, setMethod] = useState("");
  const [direction, setDirection] = useState("none");

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setNoteType("internal");
      setContent("");
      setMethod("");
      setDirection("none");
    }
    onOpenChange(newOpen);
  };

  const isValid = content.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="dialog-add-note">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>
            Add a note to the complaint timeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Note Type</Label>
            <Select value={noteType} onValueChange={setNoteType}>
              <SelectTrigger data-testid="select-note-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="communication">Communication</SelectItem>
                <SelectItem value="investigation">Investigation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="note-content">
              Content <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="note-content"
              data-testid="input-note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Note content..."
              rows={4}
            />
          </div>
          {noteType === "communication" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="comm-method">Communication Method</Label>
                <Input
                  id="comm-method"
                  data-testid="input-communication-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  placeholder="e.g. Email, Phone"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Direction</Label>
                <Select value={direction} onValueChange={setDirection}>
                  <SelectTrigger data-testid="select-communication-direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-submit-note"
            onClick={() => onSubmit(noteType, content, method, direction)}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? "Adding..." : "Add Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

function ComplaintDetailContent() {
  const [, params] = useRoute("/compliance/complaints/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const complaintId = params?.id ?? "";

  // Dialogs state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAcknowledgeOpen, setIsAcknowledgeOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [isEscalateOpen, setIsEscalateOpen] = useState(false);
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const { data: complaint, isLoading } = useQuery<ComplaintDetail>({
    queryKey: [`/api/compliance/complaints/${complaintId}`],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/compliance/complaints/${complaintId}`
      );
      if (!response.ok) throw new Error("Failed to fetch complaint");
      return response.json();
    },
    enabled: !!complaintId,
  });

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const acknowledgeMutation = useMutation({
    mutationFn: async ({
      method,
      date,
    }: {
      method: string;
      date: string;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/complaints/${complaintId}/acknowledge`,
        { acknowledgementMethod: method, acknowledgementDate: date }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to acknowledge complaint");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/complaints/${complaintId}`],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/complaints"],
      });
      toast({ title: "Complaint acknowledged" });
      setIsAcknowledgeOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async ({
      outcome,
      outcomeSummary,
      remediesProvided,
      afcaRightsAdvised,
    }: {
      outcome: string;
      outcomeSummary: string;
      remediesProvided: string;
      afcaRightsAdvised: boolean;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/complaints/${complaintId}/resolve`,
        { outcome, outcomeSummary, remediesProvided: remediesProvided || undefined, afcaRightsAdvised }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to resolve complaint");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/complaints/${complaintId}`],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/complaints"],
      });
      toast({ title: "Complaint resolved" });
      setIsResolveOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const escalateMutation = useMutation({
    mutationFn: async ({
      afcaReference,
      referralDate,
    }: {
      afcaReference: string;
      referralDate: string;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/complaints/${complaintId}/escalate`,
        { afcaReference, referralDate }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to escalate complaint");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/complaints/${complaintId}`],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/complaints"],
      });
      toast({ title: "Complaint escalated to AFCA" });
      setIsEscalateOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async ({
      noteType,
      noteContent,
      communicationMethod,
      communicationDirection,
    }: {
      noteType: string;
      noteContent: string;
      communicationMethod?: string;
      communicationDirection?: string;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/complaints/${complaintId}/notes`,
        {
          noteType,
          noteContent,
          communicationMethod: communicationMethod || undefined,
          communicationDirection:
            communicationDirection === "none"
              ? undefined
              : communicationDirection || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to add note");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/complaints/${complaintId}`],
      });
      toast({ title: "Note added" });
      setIsAddNoteOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-lg font-medium text-muted-foreground">
          Complaint not found
        </p>
        <Button
          variant="outline"
          onClick={() => setLocation("/compliance/complaints")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Complaints
        </Button>
      </div>
    );
  }

  const isOpen =
    complaint.complaintStatus === "open" ||
    complaint.complaintStatus === "acknowledged" ||
    complaint.complaintStatus === "investigating";
  const isResolved = complaint.complaintStatus === "resolved";
  const isEscalated = complaint.complaintStatus === "escalated";
  const isAcknowledged =
    complaint.complaintStatus !== "open" || !!complaint.acknowledgedDate;

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <button
              onClick={() => setLocation("/compliance/complaints")}
              className="hover:text-foreground transition-colors"
            >
              Complaints
            </button>
            <span>/</span>
            <span
              className="text-foreground font-medium"
              data-testid="complaint-reference"
            >
              {complaint.complaintReference ?? complaint.id.slice(0, 8)}
            </span>
          </div>
          {/* Title row */}
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {complaint.complainantName ?? "Unknown Complainant"}
            </h1>
            <StatusBadge status={complaint.complaintStatus} />
          </div>
          <p className="text-sm text-muted-foreground">
            {TYPE_LABELS[complaint.complaintType] ?? complaint.complaintType} ·{" "}
            {CATEGORY_LABELS[complaint.complaintCategory] ??
              complaint.complaintCategory}{" "}
            · Received {formatDate(complaint.receivedDate)}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isOpen && !isAcknowledged && (
            <Button
              variant="outline"
              onClick={() => setIsAcknowledgeOpen(true)}
              data-testid="button-acknowledge"
            >
              <Check className="mr-2 h-4 w-4" />
              Acknowledge
            </Button>
          )}
          {isAcknowledged && !isResolved && !isEscalated && (
            <Button
              variant="outline"
              data-testid="button-acknowledge"
              disabled
            >
              <Check className="mr-2 h-4 w-4" />
              Acknowledged
            </Button>
          )}
          {!isResolved && !isEscalated && (
            <Button
              variant="outline"
              onClick={() => setIsResolveOpen(true)}
              data-testid="button-resolve"
            >
              <FileText className="mr-2 h-4 w-4" />
              Resolve
            </Button>
          )}
          {!isEscalated && !isResolved && (
            <Button
              variant="outline"
              onClick={() => setIsEscalateOpen(true)}
              data-testid="button-escalate"
            >
              <ShieldAlert className="mr-2 h-4 w-4" />
              Escalate to AFCA
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setIsAddNoteOpen(true)}
            data-testid="button-add-note"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Note
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsEditModalOpen(true)}
            data-testid="button-edit-complaint"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left: Summary + Timeline */}
        <div className="lg:col-span-2 space-y-4">
          {/* Summary Card */}
          <Card data-testid="complaint-summary-card">
            <CardHeader>
              <CardTitle className="text-base">Complaint Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Complainant Name
                  </div>
                  <div className="font-medium">
                    {complaint.complainantName ?? "-"}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Complainant Type
                  </div>
                  <div className="capitalize">
                    {complaint.complainantType ?? "-"}
                  </div>
                </div>
                {complaint.complainantEmail && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                      Email
                    </div>
                    <div>{complaint.complainantEmail}</div>
                  </div>
                )}
                {complaint.complainantPhone && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                      Phone
                    </div>
                    <div>{complaint.complainantPhone}</div>
                  </div>
                )}
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Type
                  </div>
                  <div>
                    {TYPE_LABELS[complaint.complaintType] ??
                      complaint.complaintType}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Category
                  </div>
                  <div>
                    {CATEGORY_LABELS[complaint.complaintCategory] ??
                      complaint.complaintCategory}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Date Received
                  </div>
                  <div>{formatDate(complaint.receivedDate)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Reference
                  </div>
                  <div className="font-mono text-sm">
                    {complaint.complaintReference ?? "-"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Acknowledgement details (when acknowledged) */}
          {complaint.acknowledgedDate && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Acknowledged
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                  Date
                </div>
                <div>{formatDate(complaint.acknowledgedDate)}</div>
              </CardContent>
            </Card>
          )}

          {/* Resolution details (when resolved) */}
          {isResolved && complaint.outcome && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-600" />
                  Resolution
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Outcome
                  </div>
                  <div className="font-medium">
                    {OUTCOME_LABELS[complaint.outcome] ?? complaint.outcome}
                  </div>
                </div>
                {complaint.resolutionSummary && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                      Summary
                    </div>
                    <div>{complaint.resolutionSummary}</div>
                  </div>
                )}
                {complaint.remediationActions && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                      Remedies Provided
                    </div>
                    <div>{complaint.remediationActions}</div>
                  </div>
                )}
                {complaint.resolvedDate && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                      Resolved Date
                    </div>
                    <div>{formatDate(complaint.resolvedDate)}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AFCA Referral section */}
          {complaint.escalatedToAfca && complaint.afcaReferrals.length > 0 && (
            <Card data-testid="section-afca-referral">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-red-600" />
                  AFCA Referral
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {complaint.afcaReferrals.map((referral) => (
                  <div key={referral.id} className="space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                          AFCA Reference
                        </div>
                        <div className="font-mono font-medium">
                          {referral.afcaReference ?? "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                          Status
                        </div>
                        <div className="capitalize">{referral.afcaStatus}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                          Referral Date
                        </div>
                        <div>{formatDate(referral.referralDate)}</div>
                      </div>
                      {referral.responseDueDate && (
                        <div>
                          <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                            Response Due
                          </div>
                          <div>{formatDate(referral.responseDueDate)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Notes Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Timeline
                {complaint.notes.length > 0 && (
                  <span className="text-xs text-muted-foreground font-normal">
                    ({complaint.notes.length} note
                    {complaint.notes.length !== 1 ? "s" : ""})
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid="complaint-timeline">
                {complaint.notes.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No notes yet.{" "}
                    <button
                      onClick={() => setIsAddNoteOpen(true)}
                      className="text-primary hover:underline"
                    >
                      Add the first note
                    </button>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {complaint.notes.map((note, idx) => (
                      <div
                        key={note.id}
                        className={idx === complaint.notes.length - 1 ? "" : ""}
                      >
                        <TimelineItem note={note} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Deadline Card */}
          <DeadlineCard complaint={complaint} />

          {/* Metadata Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                  Created
                </div>
                <div>{formatDateTime(complaint.createdAt)}</div>
              </div>
              {complaint.dealId && (
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-0.5">
                    Linked Deal
                  </div>
                  <button
                    onClick={() => setLocation(`/deals/${complaint.dealId}`)}
                    className="text-primary hover:underline text-sm font-mono"
                    data-testid="link-deal"
                  >
                    {complaint.dealId.slice(0, 8)}…
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <AcknowledgeDialog
        open={isAcknowledgeOpen}
        onOpenChange={setIsAcknowledgeOpen}
        onSubmit={(method, date) =>
          acknowledgeMutation.mutate({ method, date })
        }
        isPending={acknowledgeMutation.isPending}
      />

      <ResolveDialog
        open={isResolveOpen}
        onOpenChange={setIsResolveOpen}
        onSubmit={(outcome, summary, remedies, afcaRights) =>
          resolveMutation.mutate({
            outcome,
            outcomeSummary: summary,
            remediesProvided: remedies,
            afcaRightsAdvised: afcaRights,
          })
        }
        isPending={resolveMutation.isPending}
      />

      <EscalateDialog
        open={isEscalateOpen}
        onOpenChange={setIsEscalateOpen}
        onSubmit={(afcaRef, referralDate) =>
          escalateMutation.mutate({ afcaReference: afcaRef, referralDate })
        }
        isPending={escalateMutation.isPending}
      />

      <AddNoteDialog
        open={isAddNoteOpen}
        onOpenChange={setIsAddNoteOpen}
        onSubmit={(noteType, content, method, direction) =>
          addNoteMutation.mutate({
            noteType,
            noteContent: content,
            communicationMethod: method,
            communicationDirection: direction,
          })
        }
        isPending={addNoteMutation.isPending}
      />

      <ComplaintModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        editingComplaint={complaint}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: [`/api/compliance/complaints/${complaintId}`],
          });
        }}
      />
    </div>
  );
}

export default function ComplaintDetailPage() {
  return (
    <ComplianceLayout>
      <ComplaintDetailContent />
    </ComplianceLayout>
  );
}
